import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface InstallPromptState {
  /** O navegador suporta o prompt de instalação programático (Android Chrome, etc). */
  canInstall: boolean;
  /** Já está rodando como app instalado (PWA standalone). */
  isInstalled: boolean;
  /** É iOS — precisa instruir o usuário manualmente (Safari → Compartilhar → "Adicionar à Tela de Início"). */
  isIOS: boolean;
  /** Dispara o prompt nativo. Resolve `true` se o usuário aceitou. */
  install: () => Promise<boolean>;
}

/**
 * Hook que abstrai a instalação como PWA.
 * - Android Chrome / Edge: dispara prompt nativo via beforeinstallprompt.
 * - iOS Safari: não tem API; o componente que usa o hook deve mostrar
 *   instruções manuais quando `isIOS && !isInstalled`.
 */
export function useInstallPrompt(): InstallPromptState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Detecta se já está rodando como app instalado.
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    setIsInstalled(standalone);

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    if (!deferred) return false;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice.outcome === 'accepted') {
        setIsInstalled(true);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [deferred]);

  const isIOS =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as Window & { MSStream?: unknown }).MSStream;

  return {
    canInstall: Boolean(deferred) && !isInstalled,
    isInstalled,
    isIOS,
    install,
  };
}
